import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './VillageChoice.module.scss';

import Flag from '../../utils/Flags/Flags';

// APIS (à adapter à vos endpoints réels)
import { getAllVillages } from '../../utils/api/villagesAPI';
import { getAllParcoursByVillage } from '../../utils/api/parcoursAPI';

import { LanguageContext } from '../../context/LanguageContext';
// Import des traductions 
import translations from '../../utils/translations/VillageChoiceTranslations';

const VillageChoice = () => {

  // Utilisation du contexte de langue global et accès simplifié aux traductions
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.fr;
  
  const navigate = useNavigate();

// -------------------- STATES -------------------------

  const [showForm, setShowForm] = useState(true);
  const [showQuitModal, setShowQuitModal] = useState(false);

  const [villages, setVillages] = useState([]);
  const [selectedVillageId, setSelectedVillageId] = useState('');

  const [parcours, setParcours] = useState([]);
  const [selectedParcoursId, setSelectedParcoursId] = useState('');

  const [showAllParcours, setShowAllParcours] = useState(false);

  // Logique de filtrage
  const activeParcours = parcours.filter(p => parseInt(p.active) === 1);
  const hasAnyActive   = activeParcours.length > 0;
  const hasInLang      = activeParcours.some(p => p.lang === language);
  const filteredParcours = showAllParcours
    ? activeParcours
    : activeParcours.filter(p => p.lang === language);

// -------------------- EFFECTS -------------------------

  useEffect(() => {
    const fetchVillages = async () => {
      try {
        const fetchedVillages = await getAllVillages();
        const formattedVillages = fetchedVillages.map((item) => ({
          id: item.id,
          name: item.nom,
        }));
        setVillages(formattedVillages);
      } catch (error) {
        console.error('Erreur lors du chargement des données', error);
      }
    };
    fetchVillages();
  }, []);

  useEffect(() => {
    const parcoursId = localStorage.getItem("currentParcoursId");
    const villageId = localStorage.getItem("currentVillageId");

    if (parcoursId && villageId) {
      setSelectedParcoursId(parcoursId);
      setSelectedVillageId(villageId);
      setShowForm(false);
    }
  },[]);

  // Effet pour réinitialiser le showAllParcours quand la langue change
  useEffect(() => {
    // Si on a des parcours et un village sélectionné, on vérifie si le parcours sélectionné
    // existe dans la nouvelle langue
    if (parcours.length > 0 && selectedVillageId) {
      // Réinitialiser l'affichage à la langue actuelle
      setShowAllParcours(false);
      
      // Si le parcours actuellement sélectionné n'est pas dans la langue actuelle,
      // et qu'on n'affiche plus tous les parcours, il faut le désélectionner
      if (selectedParcoursId && !showAllParcours) {
        const currentParcours = parcours.find(p => p.id === selectedParcoursId);
        if (currentParcours && currentParcours.lang !== language) {
          setSelectedParcoursId('');
        }
      }
    }
  }, [language]);

// -------------------- EVENTS -------------------------

  // Lorsque l'utilisateur sélectionne un village
  const handleVillageChange = async (event) => {
    const villageId = event.target.value;
    setSelectedVillageId(villageId);
    setSelectedParcoursId('');
    setParcours([]);
    // Réinitialiser l'affichage des parcours à la langue actuelle
    setShowAllParcours(false);

    try {
      const response = await getAllParcoursByVillage(villageId);
      const formattedParcours = response.map((item) => ({
        id: item.id,
        name: item.nom,
        lang: item.lang, // S'assurer que cette propriété est présente dans la réponse API
        active: item.active
      }));

      setParcours(formattedParcours);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Gère la sélection d'un parcours
  const handleParcoursChange = (event) => {
    setSelectedParcoursId(event.target.value);
  };

  // Action du bouton Démarrer
  const handleStart = () => {
    navigate(`/start-game?village=${selectedVillageId}&parcours=${selectedParcoursId}`);
  };

  const handleReprendre = () => {
    navigate(`/start-game?village=${selectedVillageId}&parcours=${selectedParcoursId}`);
  };

  const handleQuit = () => {
    // Afficher la modale de confirmation
    setShowQuitModal(true);
  };

  // -------------------- UTILS -------------------------
  // Langues supportées pour le cache villages
  const LANGS = useMemo(() => (['fr','en','de','es','it']), []);

  // Purge centralisée de tout le cache et de la sélection pour un parcours villages
  const purgeVillageCache = (parcoursId) => {
    if (!parcoursId) return;
    try {
      // Progression & structure
      localStorage.removeItem(`progression_${parcoursId}`);
      localStorage.removeItem(`questionsStructure_${parcoursId}`);
      // Sélection courante
      localStorage.removeItem("currentVillageId");
      localStorage.removeItem("currentParcoursId");
      // localStorage.removeItem("questionIndex"); // Vérifier si toujours utilisé...
      // Questions par langue
      LANGS.forEach(l =>
        localStorage.removeItem(`questions_villages_${parcoursId}`)
      );
      // Réponses par langue (repLibres / repQcm)
      LANGS.forEach(l => {
        localStorage.removeItem(`repLibres_villages_${parcoursId}`);
        localStorage.removeItem(`repQcm_villages_${parcoursId}`);
      });
      // Langue mémorisée pour ce parcours (si utilisée)
      localStorage.removeItem(`gameLang-${parcoursId}`);
    } catch (e) {
      console.warn('Purge villages: erreur non bloquante', e);
    }
  };

// -------------------- FUNCTIONS -------------------------

  const confirmQuit = () => {
    // Réinitialiser les données et masquer la modale
    const pId = localStorage.getItem("currentParcoursId");
    purgeVillageCache(pId);
    setSelectedParcoursId('');
    setSelectedVillageId('');
    setShowForm(true);
    setShowQuitModal(false);
    // Réinitialiser l'affichage des parcours à la langue actuelle
    setShowAllParcours(false);
  };

// -------------------- DISPLAY -------------------------

  return (
    <div className={styles.container}>
      <div className={styles.illustrationFrame}>
        <img className={styles.illustration} src="/assets/img/village1-800x450.png" alt="Illustration village" />
      </div>

      {/* <p className={styles.paragraph}> <strong>À la découverte de nos villages</strong><span> est une initiative pensée et réfléchie en partenariat avec les Mairies. Accédez-y gratuitement et rejoignez nos parcours ludiques !</span></p> */}
      
      {showForm ? (
        <>
          <div className={styles.selectGroup}>
            <label htmlFor="village-select" className={styles.label}>{t.selectVillageLabel}</label>
            <select
              id="village-select"
              className={styles.select}
              value={selectedVillageId}
              onChange={handleVillageChange}
            >
              <option value="" disabled>{t.selectVillagePlaceholder}</option>
              {villages.map((village) => (
                <option key={village.id} value={village.id}>
                  {village.name}
                </option>
              ))}
            </select>
          </div>

          {/* Zone parcours : affichage selon la présence/ langue */}
          {selectedVillageId && (
            <>
              {/* Aucun parcours actif dans ce village */}
              {!hasAnyActive && (
                <p className={styles.infoMessage}>{t.noParcoursAvailable}</p>
              )}

              {/* Il y a des parcours actifs mais aucun dans la langue courante */}
              {hasAnyActive && !hasInLang && !showAllParcours && (
                <div className={styles.messageBox}>
                  <p className={styles.infoMessage}>
                    {t.noLangParcoursAvailable}<br />
                    <button
                      type="button"
                      className={styles.loadAllButton}
                      onClick={() => setShowAllParcours(true)}
                    >
                      {t.loadAllParcoursButton || "Afficher tous les parcours"}
                    </button>
                  </p>
                </div>
              )}

              {/* Sélecteur de parcours si langue ok OU si on affiche tout */}
              {hasAnyActive && (hasInLang || showAllParcours) && (
                <div className={styles.selectGroup}>
                  <label htmlFor="parcours-select" className={styles.label}>{t.selectParcoursLabel}</label>
                  <select
                    id="parcours-select"
                    className={styles.select}
                    value={String(selectedParcoursId)}
                    onChange={handleParcoursChange}
                  >
                    <option value="" disabled>{t.selectParcoursPlaceholder}</option>
                    {filteredParcours.map((p) => (
                      <option key={String(p.id)} value={String(p.id)}>
                        <Flag lang={p.lang} /> - {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* Bouton "Démarrer" : n'apparaît que si un parcours est sélectionné */}
          {selectedParcoursId && (showAllParcours || hasInLang) && (
            <button
              type="button"
              onClick={handleStart}
              className={styles.startButton}
            >
              {t.startButton}
            </button>
          )}
        </>
      ) : ( 
        <div className={styles.enCoursContainer}>
          <div className={styles.enCours}>
            <p>{t.resumeMessage}</p>
            <div className={styles.buttonGroup}>
              <button className={styles.resumeButton} onClick={handleReprendre}>
                {t.resumeButton}
              </button>
              <button className={styles.quitButton} onClick={handleQuit}>
                {t.quitButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation d'abandon */}
      {showQuitModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <p>{t.quitConfirmationMessage}</p>
            <button className={styles.confirmButton} onClick={confirmQuit}>
              {t.confirmQuitButton}
            </button>
            <button className={styles.cancelButton} onClick={() => setShowQuitModal(false)}>
              {t.cancelQuitButton}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VillageChoice;